import { h } from '@omni-torn/ui';
import {
  cleanItemSnap,
  compareDates,
  critRate,
  ITEM_STAT_CATEGORIES,
  ITEM_STATS_BATCH,
  ITEM_USAGE,
  itemsCsvRows,
  itemSnapEntries,
  itemTracks,
  measureOf,
  measureSeries,
  readEquipment,
  readInventory,
  readItemDetails,
  readItemStats,
  savedDates,
  snapsFromRead,
  type ItemInfo,
  type ItemMeasure,
  type ItemSnap,
  type ItemStatCategory,
  type ItemTrack,
} from '@omni-torn/shared';
import { csvButton, filterSelect } from '../log-kit/ui';
import { ownHistoryOn, ownRead, ownSend } from '../own-history';
import { session, torn, tornCallsLeft } from '../session';
import { ago, box, ext, facts, lineChart, loading, num, problem, profileUrl, table } from '../ui';

/**
 * Item stats (You → Item stats; website roadmap S39 — torn.report's Item Stats): your weapons and armor with their use over
 * time — hits, critical hits, damage, rounds fired, hits taken, first owner, when made, bonuses — two saved dates side by
 * side, new items marked. Pure logic in packages/shared item-stats.ts.
 *
 * Torn calls, with your key from this browser (or through the extension): on opening, your equipment and the use of what
 * you have on and every copy kept before (2 calls) — none when the newest reading is under 10 minutes old; after that one
 * call a click ("Next read": one weapon or armor category of your inventory, the details of up to 50 copies, or their use).
 * Each use read is a snapshot per item: from terms 19 kept for you on the Omni-Torn server (own-history stream `items`,
 * with what the extension saw on your own Items page while you viewed it), below 19 in this browser only.
 */

const FRESH_MS = 10 * 60_000;
const LOCAL_MAX = 5_000;
const CAT_NAMES: Record<ItemStatCategory, string> = { Primary: 'primary weapons', Secondary: 'secondary weapons', Melee: 'melee weapons', Defensive: 'armor' };
const WEAPON_MEASURES: ReadonlyArray<readonly [ItemMeasure, string]> = [
  ['hits', 'Hits'],
  ['criticalHits', 'Critical hits'],
  ['damage', 'Damage'],
  ['roundsFired', 'Rounds fired'],
  ['finishingHits', 'Finishing hits'],
  ['misses', 'Misses'],
  ['respectEarned', 'Respect earned'],
];
const ARMOR_MEASURES: ReadonlyArray<readonly [ItemMeasure, string]> = [
  ['hitsReceived', 'Hits taken'],
  ['damageTaken', 'Damage taken'],
  ['damageMitigated', 'Damage mitigated'],
];
const ALL_MEASURES: ReadonlyArray<readonly [ItemMeasure, string]> = [...WEAPON_MEASURES, ...ARMOR_MEASURES, ['quality', 'Quality'], ['statDamage', 'Weapon damage stat'], ['accuracy', 'Accuracy'], ['armor', 'Armor stat']];

/** What this visit read (per player): copies known, which categories and which copies' details and use. */
interface Visit {
  known: Map<number, ItemInfo>;
  cats: Set<ItemStatCategory>;
  details: Set<number>;
  usage: Set<number>;
}
const visits = new Map<number, Visit>();

const localKey = (pid: number) => `omnitorn.itemstats.${pid}`;
function loadLocal(pid: number): ItemSnap[] {
  try {
    const raw = JSON.parse(localStorage.getItem(localKey(pid)) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.map((x) => cleanItemSnap(x)).filter((s): s is ItemSnap => !!s) : [];
  } catch {
    return [];
  }
}
function saveLocal(pid: number, snaps: readonly ItemSnap[]): void {
  try {
    localStorage.setItem(localKey(pid), JSON.stringify([...snaps].sort((a, b) => a.timestamp - b.timestamp).slice(-LOCAL_MAX)));
  } catch {
    /* no storage (private window): this visit only */
  }
}

const tile = (label: string, value: string, data = '', title = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}), ...(title ? { title } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value' }, value));
const dateText = (ms: number) => `${new Date(ms).toISOString().slice(0, 16).replace('T', ' ')} TCT`;
const dayText = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const usageLabel = (m: ItemMeasure) => ALL_MEASURES.find(([k]) => k === m)?.[1] ?? m;

export async function renderItemStats(view: HTMLElement): Promise<void> {
  const me = session.get();
  if (!me) return;
  const pid = me.playerId;
  const onServer = ownHistoryOn();
  const visit: Visit = visits.get(pid) ?? { known: new Map(), cats: new Set(), details: new Set(), usage: new Set() };
  visits.set(pid, visit);
  const q = new URLSearchParams(location.search);
  let kindFilter = q.get('kind') === 'weapon' || q.get('kind') === 'armor' ? q.get('kind')! : '';
  let chartItem = q.get('item') ?? '';
  let chartMeasure: ItemMeasure = (ALL_MEASURES.find(([m]) => m === q.get('measure'))?.[0] ?? 'hits') as ItemMeasure;
  let cmpA = Number(q.get('a')) || 0;
  let cmpB = Number(q.get('b')) || 0;
  const setQ = () => {
    const p = new URLSearchParams(location.search);
    const put = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k));
    put('kind', kindFilter);
    put('item', chartItem);
    put('measure', chartMeasure === 'hits' ? '' : chartMeasure);
    put('a', cmpA ? String(cmpA) : '');
    put('b', cmpB ? String(cmpB) : '');
    const s = p.toString();
    history.replaceState(history.state, '', `${location.pathname}${s ? `?${s}` : ''}`);
  };

  const body = h('div', { 'data-itemstats': '' }, loading('Reading your weapons and armor…'));
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Item stats'), h('p', { class: 'muted' }, onServer ? 'Your weapons and armor with their use over time. Every reading is kept for you on Omni-Torn (only you see it), so you can compare any two dates.' : 'Your weapons and armor with their use over time. Every reading is kept in this browser, so you can compare any two dates; nothing is sent to the Omni-Torn server.')),
    body,
  );

  // ------------------------------------------------------------ what's kept

  let snaps: ItemSnap[] = [];
  const problems: string[] = [];
  if (onServer) {
    try {
      // Newest first, 2,000 at a time; a page ends inside one reading's second, so the next starts again at that second.
      let before: number | undefined;
      for (let page = 0; page < 25; page++) {
        const r = await ownRead('items', { limit: 2000, ...(before ? { before } : {}) });
        const got = Object.values(r.entries).map((x) => cleanItemSnap(x)).filter((s): s is ItemSnap => !!s);
        snaps.push(...got);
        if (!r.more || !got.length) break;
        const next = (Math.min(...got.map((s) => s.timestamp)) + 1) * 1000;
        if (before !== undefined && next >= before) break;
        before = next;
      }
    } catch (err) {
      problems.push(`Your kept readings couldn’t be read: ${(err as Error)?.message ?? 'no answer'}`);
    }
  } else snaps = loadLocal(pid);
  const dedupe = () => {
    const seen = new Map<string, ItemSnap>();
    for (const s of snaps) seen.set(`${s.source}:${s.uid ?? `p${s.item_id}-${s.slot ?? ''}`}:${s.timestamp}`, s);
    snaps = [...seen.values()];
  };
  dedupe();

  /** A use read's snapshots: kept (server or this browser) and shown. */
  const keep = async (fresh: ItemSnap[]) => {
    if (!fresh.length) return;
    snaps.push(...fresh);
    dedupe();
    if (onServer) {
      const r = await ownSend('items', itemSnapEntries(fresh), null);
      if (!r) problems.push('This reading couldn’t be kept on Omni-Torn right now — it shows here for this visit.');
    } else saveLocal(pid, snaps);
  };

  // What older readings knew of each copy (so a use read can carry stats and bonuses even before they're read again).
  const knownFromSnaps = () => {
    for (const t of itemTracks(snaps)) {
      const s = t.latest;
      if (!s.uid || visit.known.has(s.uid)) continue;
      visit.known.set(s.uid, { uid: s.uid, item_id: s.item_id, name: s.name, kind: s.kind, ...(s.sub_type ? { sub_type: s.sub_type } : {}), ...(s.stats ? { stats: s.stats } : {}), ...(s.bonuses ? { bonuses: s.bonuses } : {}), ...(s.rarity ? { rarity: s.rarity } : {}) });
    }
  };
  knownFromSnaps();

  // ------------------------------------------------------------ Torn reads (one call each)

  const learn = (infos: readonly ItemInfo[], details = false) => {
    for (const i of infos) {
      const was = visit.known.get(i.uid);
      visit.known.set(i.uid, { ...was, ...i, kind: i.kind ?? was?.kind ?? null, ...(was?.slot && i.slot === undefined ? { slot: was.slot } : {}), ...(was?.equipped !== undefined && i.equipped === undefined ? { equipped: was.equipped } : {}) });
      if (details || i.stats) visit.details.add(i.uid);
    }
  };
  const readEquip = async () => {
    const infos = readEquipment(await torn('/v2/user/equipment', {}, 30_000));
    // What isn't on any more isn't "equipped" now.
    for (const k of visit.known.values()) if (k.equipped) k.equipped = false;
    for (const k of visit.known.values()) delete k.slot;
    learn(infos);
    return infos.length;
  };
  const readUsage = async (uids: number[]) => {
    const batch = uids.slice(0, ITEM_STATS_BATCH);
    if (!batch.length) return 0;
    const usage = readItemStats(await torn(`/v2/torn/${batch.join(',')}/itemstats`, {}, 0));
    for (const u of batch) visit.usage.add(u);
    for (const u of usage) {
      const k = visit.known.get(u.uid);
      if (!k) visit.known.set(u.uid, { uid: u.uid, item_id: u.item_id, name: u.name, kind: u.kind, ...(u.sub_type ? { sub_type: u.sub_type } : {}) });
      else if (!k.kind && u.kind) k.kind = u.kind;
    }
    await keep(snapsFromRead(Date.now(), usage, visit.known));
    return usage.length;
  };
  const readDetails = async (uids: number[]) => {
    const batch = uids.slice(0, ITEM_STATS_BATCH);
    const infos = readItemDetails(await torn(`/v2/torn/${batch.join(',')}/itemdetails`, {}, 10 * 60_000));
    for (const u of batch) visit.details.add(u);
    learn(infos, true);
    return infos.length;
  };
  const readCat = async (cat: ItemStatCategory) => {
    const r = readInventory(await torn('/v2/user/inventory', { cat, limit: '250' }, 10 * 60_000), cat);
    visit.cats.add(cat);
    const before = visit.known.size;
    learn(r.items);
    return { found: r.items.length, added: visit.known.size - before };
  };

  // On opening: your equipment and the use of every copy known (2 calls) — not when the newest reading is fresh.
  const newestApi = Math.max(0, ...snaps.filter((s) => s.source === 'api').map((s) => s.timestamp * 1000));
  let readNote = '';
  if (!visit.usage.size && Date.now() - newestApi > FRESH_MS) {
    try {
      const on = await readEquip();
      const uids = [...visit.known.values()].filter((k) => k.kind).sort((a, b) => Number(!!b.equipped) - Number(!!a.equipped)).map((k) => k.uid);
      const n = await readUsage(uids);
      readNote = `Read now: ${num(on)} item${on === 1 ? '' : 's'} on you, the use of ${num(n)} cop${n === 1 ? 'y' : 'ies'} (2 Torn calls).`;
    } catch (err) {
      problems.push(`Torn couldn’t be read: ${(err as Error)?.message ?? 'no answer'}`);
    }
  } else if (newestApi) readNote = `Your newest reading is from ${ago(newestApi)} — read again with the button when you like.`;

  /** The next single read a click makes (one Torn call), or null when everything this visit can read was read. */
  const nextStep = (): { label: string; run: () => Promise<string> } | null => {
    const cat = ITEM_STAT_CATEGORIES.find((c) => !visit.cats.has(c));
    if (cat)
      return {
        label: `Find your ${CAT_NAMES[cat]} (1 call)`,
        run: async () => {
          const r = await readCat(cat);
          return `Your ${CAT_NAMES[cat]}: ${num(r.found)} with their own id${r.added ? `, ${num(r.added)} not read before` : ''}.`;
        },
      };
    const noDetails = [...visit.known.values()].filter((k) => !k.stats && !visit.details.has(k.uid)).map((k) => k.uid);
    if (noDetails.length)
      return {
        label: `Read damage, accuracy and bonuses of ${num(Math.min(noDetails.length, ITEM_STATS_BATCH))} item${noDetails.length === 1 ? '' : 's'} (1 call)`,
        run: async () => `Details of ${num(await readDetails(noDetails))} item(s) read.`,
      };
    const unread = [...visit.known.values()].filter((k) => !visit.usage.has(k.uid)).map((k) => k.uid);
    if (unread.length)
      return {
        label: `Read the use of ${num(Math.min(unread.length, ITEM_STATS_BATCH))} more item${unread.length === 1 ? '' : 's'} (1 call)`,
        run: async () => `The use of ${num(await readUsage(unread))} item(s) read and saved.`,
      };
    return null;
  };

  // ------------------------------------------------------------ drawing

  function draw(): void {
    const now = Date.now();
    const all = itemTracks(snaps, now);
    const tracks = all.filter((t) => !kindFilter || t.kind === kindFilter);
    const weapons = tracks.filter((t) => t.kind === 'weapon');
    const armor = tracks.filter((t) => t.kind === 'armor');
    const dates = savedDates(snaps);
    const lastApi = Math.max(0, ...snaps.filter((s) => s.source === 'api').map((s) => s.timestamp * 1000));
    const sum = (list: ItemTrack[], m: ItemMeasure) => list.reduce((a, t) => a + (measureOf(t.latest, m) ?? 0), 0);
    const allWeapons = all.filter((t) => t.kind === 'weapon');
    const allArmor = all.filter((t) => t.kind === 'armor');

    // The next read (one call a click), what's left of the minute, and what the last one did.
    const step = nextStep();
    const again = !step && visit.known.size ? { label: `Save a new reading — the use of ${num(Math.min(visit.known.size, ITEM_STATS_BATCH))} item${visit.known.size === 1 ? '' : 's'} (1 call)`, run: async () => {
      visit.usage.clear();
      return `The use of ${num(await readUsage([...visit.known.keys()]))} item(s) read and saved.`;
    } } : null;
    const todo = step ?? again;
    const left = tornCallsLeft();
    const nextBtn = h('button', { type: 'button', class: 'btn-small', 'data-action': 'itemstats-next', ...(todo && left > 0 ? {} : { disabled: true }) }, todo ? todo.label : 'Nothing to read') as HTMLButtonElement;
    // A long label wraps on a phone (the site's CSP allows styles set from script, not style attributes).
    nextBtn.style.whiteSpace = 'normal';
    nextBtn.style.textAlign = 'left';
    const note = h('span', { class: 'muted small', 'data-itemstats-note': '' }, readNote);
    nextBtn.addEventListener('click', async () => {
      if (!todo) return;
      nextBtn.disabled = true;
      note.textContent = ' Reading…';
      try {
        readNote = await todo.run();
      } catch (err) {
        readNote = `Torn couldn’t be read: ${(err as Error)?.message ?? 'no answer'}`;
      }
      if (body.isConnected) draw();
    });
    const reads = box('Your readings', [
      h('div', { class: 'toolbar' }, nextBtn, note),
      h('p', { class: 'muted small' }, `${left} of this site’s Torn calls left this minute. Each click is one Torn call: one weapon or armor category of your inventory, the details of up to ${ITEM_STATS_BATCH} items, or their use (a new saved reading).${onServer ? ' The extension adds what it sees on your own Items page while you look at it.' : ''}`),
    ], { id: 'itemstats-reads' });

    const tiles = h(
      'div',
      { class: 'tiles' },
      tile('Weapons', num(allWeapons.length), 'weapons'),
      tile('Armor', num(allArmor.length), 'armor'),
      tile('Saved readings', num(dates.length), 'readings', 'Every time the use of your items was read (and every Items page view the extension saw)'),
      tile('Last read', lastApi ? ago(lastApi) : '—', 'last'),
      tile('Hits (weapons)', num(sum(allWeapons, 'hits')), 'hits'),
      tile('Critical hits', num(sum(allWeapons, 'criticalHits')), 'crits'),
      tile('Damage dealt', num(sum(allWeapons, 'damage')), 'damage'),
      tile('Hits taken (armor)', num(sum(allArmor, 'hitsReceived')), 'taken'),
    );

    const filters = h(
      'div',
      { class: 'toolbar' },
      filterSelect('Show', [['', 'Weapons and armor'], ['weapon', 'Weapons'], ['armor', 'Armor']], kindFilter, (v) => {
        kindFilter = v;
        setQ();
        draw();
      }, 'kind'),
    );

    const nameCell = (t: ItemTrack) => {
      const s = t.latest;
      return h(
        'span',
        {},
        h('span', { class: s.rarity ? `rarity-text-${s.rarity}` : '', title: s.uid ? `Item ${s.item_id}, copy ${s.uid}` : `Item ${s.item_id} (from your Items page)` }, s.name),
        s.equipped ? h('span', { class: 'badge on', title: s.slot ? `On you: ${s.slot}` : 'On you' }, 'on') : null,
        t.isNew ? h('span', { class: 'badge conf-medium', 'data-new': '', title: 'Not in your earlier readings, or made in the last week' }, 'new') : null,
        !s.uid ? h('span', { class: 'badge', title: 'Seen on your own Items page by the extension: no use stats there' }, 'page') : null,
      );
    };
    const bonusCell = (t: ItemTrack) => (t.latest.bonuses?.length ? t.latest.bonuses.map((b) => (b.value === null ? b.title : `${b.title} ${num(b.value, 2)}${/disarm/i.test(b.title) ? ' turns' : '%'}`)).join(', ') : '—');
    const ownerCell = (t: ItemTrack) => {
      const o = t.latest.first_owner;
      return o ? (o === pid ? 'You' : ext(profileUrl(o), `[${o}]`)) : '—';
    };
    const createdCell = (t: ItemTrack) => (t.latest.created ? h('span', { title: dateText(t.latest.created * 1000) }, dayText(t.latest.created * 1000)) : '—');
    const v = (t: ItemTrack, m: ItemMeasure, d = 0) => num(measureOf(t.latest, m), d);
    const sortV = (m: ItemMeasure) => (t: ItemTrack) => measureOf(t.latest, m) ?? -1;
    const weaponBox = weapons.length || !kindFilter || kindFilter === 'weapon'
      ? box(`Weapons (${num(weapons.length)})`, [
          table(weapons, [
            { label: 'Weapon', get: nameCell, sort: (t) => t.name },
            { label: 'Type', get: (t) => t.latest.sub_type ?? '—', sort: (t) => t.latest.sub_type ?? '~' },
            { label: 'Bonuses', get: bonusCell, sort: (t) => bonusCell(t) },
            { label: 'Dmg', get: (t) => v(t, 'statDamage', 2), sort: sortV('statDamage'), cls: 'r' },
            { label: 'Acc', get: (t) => v(t, 'accuracy', 2), sort: sortV('accuracy'), cls: 'r' },
            { label: 'Quality', get: (t) => v(t, 'quality', 2), sort: sortV('quality'), cls: 'r' },
            { label: 'Hits', get: (t) => v(t, 'hits'), sort: sortV('hits'), cls: 'r' },
            { label: 'Crits', get: (t) => { const r = critRate(t.latest); return `${v(t, 'criticalHits')}${r === null ? '' : ` (${Math.round(r * 100)}%)`}`; }, sort: sortV('criticalHits'), cls: 'r' },
            { label: 'Damage', get: (t) => v(t, 'damage'), sort: sortV('damage'), cls: 'r' },
            { label: 'Rounds fired', get: (t) => v(t, 'roundsFired'), sort: sortV('roundsFired'), cls: 'r' },
            { label: 'Finishing', get: (t) => v(t, 'finishingHits'), sort: sortV('finishingHits'), cls: 'r' },
            { label: 'First owner', get: ownerCell, sort: (t) => t.latest.first_owner ?? 0 },
            { label: 'Made', get: createdCell, sort: (t) => t.latest.created ?? 0 },
            { label: 'Read', get: (t) => h('span', { title: dateText(t.latest.timestamp * 1000) }, ago(t.latest.timestamp * 1000)), sort: (t) => t.latest.timestamp },
          ], { empty: 'No weapons read yet.', remember: 'itemstats-weapons' }),
        ], { id: 'itemstats-weapons' })
      : null;
    const armorBox = armor.length || !kindFilter || kindFilter === 'armor'
      ? box(`Armor (${num(armor.length)})`, [
          table(armor, [
            { label: 'Armor', get: nameCell, sort: (t) => t.name },
            { label: 'Bonuses', get: bonusCell, sort: (t) => bonusCell(t) },
            { label: 'Armor', get: (t) => v(t, 'armor', 2), sort: sortV('armor'), cls: 'r' },
            { label: 'Quality', get: (t) => v(t, 'quality', 2), sort: sortV('quality'), cls: 'r' },
            { label: 'Hits taken', get: (t) => v(t, 'hitsReceived'), sort: sortV('hitsReceived'), cls: 'r' },
            { label: 'Damage taken', get: (t) => v(t, 'damageTaken'), sort: sortV('damageTaken'), cls: 'r' },
            { label: 'Mitigated', get: (t) => v(t, 'damageMitigated'), sort: sortV('damageMitigated'), cls: 'r' },
            { label: 'First owner', get: ownerCell, sort: (t) => t.latest.first_owner ?? 0 },
            { label: 'Made', get: createdCell, sort: (t) => t.latest.created ?? 0 },
            { label: 'Read', get: (t) => h('span', { title: dateText(t.latest.timestamp * 1000) }, ago(t.latest.timestamp * 1000)), sort: (t) => t.latest.timestamp },
          ], { empty: 'No armor read yet.', remember: 'itemstats-armor' }),
        ], { id: 'itemstats-armor' })
      : null;

    // One item over time.
    const pick = tracks.find((t) => t.key === chartItem) ?? tracks.find((t) => t.latest.equipped && t.kind === 'weapon') ?? tracks[0];
    const measures = pick?.kind === 'armor' ? [...ARMOR_MEASURES, ['armor', 'Armor stat'] as const, ['quality', 'Quality'] as const] : [...WEAPON_MEASURES, ['statDamage', 'Weapon damage stat'] as const, ['accuracy', 'Accuracy'] as const];
    const m = measures.some(([k]) => k === chartMeasure) ? chartMeasure : measures[0]![0];
    const chartBox = pick
      ? box(h('h2', {}, `${pick.name}: ${usageLabel(m).toLowerCase()} over time`), [
          h(
            'div',
            { class: 'toolbar' },
            filterSelect('Item', tracks.map((t) => [t.key, `${t.name}${t.uid ? ` #${t.uid}` : ''}`] as const), pick.key, (val) => {
              chartItem = val;
              setQ();
              draw();
            }, 'item'),
            filterSelect('Measure', measures, m, (val) => {
              chartMeasure = val as ItemMeasure;
              setQ();
              draw();
            }, 'measure'),
          ),
          lineChart(measureSeries(pick, m), { label: usageLabel(m), format: (x) => num(x) }),
          facts([
            ['First seen', dateText(pick.firstSeen)],
            ['Readings', num(pick.snaps.length)],
            ['First owner', ownerCell(pick)],
            ['Made', pick.latest.created ? dateText(pick.latest.created * 1000) : '—'],
          ]),
        ], { id: 'itemstats-chart' })
      : null;

    // Two saved dates side by side.
    let compareBox: HTMLElement | null = null;
    if (dates.length >= 2) {
      const b = dates.includes(cmpB) ? cmpB : dates[0]!;
      const a = dates.includes(cmpA) && cmpA !== b ? cmpA : (dates.find((d) => d < b) ?? dates[dates.length - 1]!);
      const opts = dates.map((d) => [String(d), dateText(d)] as const);
      const cmpMeasures: ItemMeasure[] = kindFilter === 'armor' ? ['hitsReceived', 'damageTaken', 'damageMitigated'] : kindFilter === 'weapon' ? ['hits', 'criticalHits', 'damage', 'roundsFired'] : ['hits', 'criticalHits', 'damage', 'roundsFired', 'hitsReceived'];
      const rows = compareDates(tracks, a, b, cmpMeasures);
      const cell = (r: (typeof rows)[number], mm: ItemMeasure) => {
        const x = r.values[mm];
        if (!x) return '—';
        const [va, vb, d] = x;
        return h('span', { title: `${num(va)} → ${num(vb)}` }, `${num(vb)}`, d ? h('span', { class: d > 0 ? 't-green' : 't-red' }, ` (${d > 0 ? '+' : '−'}${num(Math.abs(d))})`) : null);
      };
      compareBox = box('Compare two saved dates', [
        h(
          'div',
          { class: 'toolbar' },
          filterSelect('From', opts, String(a), (val) => {
            cmpA = Number(val);
            setQ();
            draw();
          }, 'cmp-a'),
          filterSelect('To', opts, String(b), (val) => {
            cmpB = Number(val);
            setQ();
            draw();
          }, 'cmp-b'),
        ),
        table(rows, [
          { label: 'Item', get: (r) => h('span', {}, nameCell(r.track), r.added ? h('span', { class: 'badge conf-high', 'data-added': '' }, 'added') : null, r.gone ? h('span', { class: 'badge conf-low' }, 'not read') : null), sort: (r) => r.track.name },
          ...cmpMeasures.map((mm) => ({ label: usageLabel(mm), get: (r: (typeof rows)[number]) => cell(r, mm), sort: (r: (typeof rows)[number]) => r.values[mm]?.[2] ?? -1, cls: 'r' })),
        ], { empty: 'Nothing read on these dates.' }),
        h('p', { class: 'muted small' }, `Each item as it was at the newer date, with the change since ${dateText(Math.min(a, b))} in brackets. “Added”: not had then; “not read”: had then but not in a reading since.`),
      ], { id: 'itemstats-compare' });
    }

    const csv = csvButton(() => `item-stats-${new Date().toISOString().slice(0, 10)}.csv`, () => itemsCsvRows(tracks));
    const notes = h('p', { class: 'muted small' }, `How it’s read: your equipment and inventory with your key (Minimal), each copy’s details and use from Torn’s item stats by its id (hits, critical hits, damage, rounds fired, finishing hits, hits and damage taken, first owner, when it was made). Every use read is saved — ${onServer ? 'for you on Omni-Torn until you delete your data' : 'in this browser (sign in on terms 19 to keep them on Omni-Torn)'} — so the tables show the newest and the chart and comparison every reading. “New”: not in your earlier readings, or made in the last week.`);
    body.replaceChildren(...([
      problems.length ? h('div', { class: 'notice bad', role: 'alert' }, problems.join(' · ')) : null,
      reads,
      filters,
      tiles,
      weaponBox ? (weaponBox.querySelector('.panel-head')?.append(csv), weaponBox) : null,
      armorBox,
      chartBox,
      compareBox,
      dates.length < 2 ? h('p', { class: 'muted', 'data-compare-note': '' }, 'Once you have two saved readings, you can compare them here.') : null,
      notes,
    ].filter(Boolean) as HTMLElement[]));
  }

  try {
    draw();
  } catch (err) {
    body.replaceChildren(problem(err));
  }
}
